import { Select } from "@radix-ui/themes";
import { Canvas } from "@react-three/fiber";
import { usePlayerStore } from "@tezzro/player";
import { useEffect, useMemo, useState } from "react";
import { Mesh, MeshBasicMaterial, Color, Vector3, Vector3Tuple } from "three";
import { useSchedules } from "@/features/annotation/lib/useSchedules";
import { useGondolasRawQuery } from "@/shared/api";
import { insightsApiInstance } from "@/shared/api/axios";
import {
  buildCameraImageMap,
  getFilteredCameraIds,
} from "@/shared/lib/map-helpers";
import { arrowTunnel } from "./arrow-tunnel";
import { CameraInstances } from "./camera-instances";
import { CustomMapControls } from "./custom-map-controls";
import { Planogram } from "./planogram";
import { useCalibrationData } from "./useCalibrationData";
import { useGltfBinary } from "./useGltfBinary";
import { Zones } from "./zones";

const modelUrl = {
  simple: (calibrationId: number) =>
    `${insightsApiInstance.defaults.baseURL}/calibrations/${calibrationId}/simple_model`,
};

export function Store3D({ onSelectCamera }: { onSelectCamera: () => void }) {
  const calibrationData = useCalibrationData();
  const [selectedGondolas, setSelectedGondolas] = useState<string[]>([]);
  const [cameraType, setCameraType] = useState("all");
  const [target, setTarget] = useState<Vector3Tuple | null>(null);
  const { getState } = usePlayerStore();

  const { activeSchedule } = useSchedules({ queryKey: "planogramSchedules" });
  const { data: gondolas } = useGondolasRawQuery({
    on_datetime: activeSchedule?.from_datetime,
  });
  const cameraImages = useMemo(() => buildCameraImageMap(gondolas), [gondolas]);

  const filteredCameras = useMemo(() => {
    if (gondolas === undefined) return [];
    return getFilteredCameraIds({
      gondolas,
      cameraType,
      selectedGondolas,
      cameraCalibrations:
        calibrationData.calibrations?.result.camera_calibrations,
    });
  }, [gondolas, selectedGondolas, cameraType, calibrationData.calibrations]);

  const [material] = useState(
    () =>
      new MeshBasicMaterial({
        wireframe: false,
        wireframeLinewidth: 1,
        color: new Color(0xffffff),
        transparent: true,
        opacity: 0.1,
      })
  );

  const [simpleModel] = useGltfBinary({
    key: `simple-${calibrationData.currentCalibrationId}`,
    url: modelUrl.simple(calibrationData.currentCalibrationId),
    skip: !calibrationData.currentCalibrationId,
    traverse: (child) => {
      if (child instanceof Mesh) {
        child.material = material;
      }
    },
  });

  useEffect(() => {
    const cameraId = getState().cameraId;
    if (!cameraId) return;

    const calibration = calibrationData.findCameraCalibration(cameraId);
    if (calibration) {
      const { location } = calibration;
      const position = new Vector3(location[0], location[2], -location[1]);
      setTarget(position.toArray());
    }
  }, [calibrationData.findCameraCalibration, getState]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div className="absolute top-4 left-4 z-10 dark:bg-black/50 backdrop-blur-sm rounded-lg p-3">
        <div className="flex items-center gap-3">
          <Select.Root value={cameraType} onValueChange={setCameraType}>
            <Select.Trigger placeholder="Camera type" />
            <Select.Content>
              <Select.Item value="all">All cameras</Select.Item>
              <Select.Item value="hand_tracking">Hand-tracking</Select.Item>
              <Select.Item value="shelf_tracking">Shelf-tracking</Select.Item>
              <Select.Item value="non-shelf">Non-shelf</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      </div>
      <arrowTunnel.Out />
      <Canvas
        key="virtual-store"
        className="bg-black"
        frameloop="demand"
        orthographic
        camera={{
          position: [0, 50, 0],
          rotation: [0, 0, 0],
          far: 10000,
          zoom: 30,
        }}
        raycaster={{
          params: {
            Points: { threshold: 0.001 },
            Mesh: { threshold: 0.001 },
            Line: { threshold: 0.001 },
            Line2: { threshold: 0.001 },
            LOD: undefined,
            Sprite: undefined,
          },
        }}
        gl={{ antialias: true, alpha: false }}
        resize={{ debounce: 0 }}
      >
        <CustomMapControls target={target} />
        <group position={[-14, 0, 5]}>
          {!!simpleModel && <primitive object={simpleModel} />}
          {!!calibrationData.calibrations?.result.camera_calibrations
            .length && (
            <CameraInstances
              filteredCameraIds={filteredCameras}
              cameras={calibrationData.calibrations.result.camera_calibrations}
              onSelectCamera={onSelectCamera}
              getCameraImage={(cameraId) => cameraImages.get(cameraId) ?? null}
            />
          )}
          <Zones />
          <Planogram
            selectedGondolas={selectedGondolas}
            onGondolaSelect={setSelectedGondolas}
          />
        </group>
      </Canvas>
    </div>
  );
}
